// Phase 2 — `stage === "password"` form (admin AND partner/merchant
// variants). Owns the email + password + Show/Hide + Sign-in submit +
// Demo button + Forgot link rendering. State + handlers come from parent.

import { Lock, Mail, Eye, EyeOff, Loader2, Sparkles, Users, Store } from "lucide-react";
import { Field, inputCls, primaryBtn, secondaryBtn } from "./LoginShared";

export default function LoginForm({
  role,
  email, setEmail,
  password, setPassword,
  showPw, setShowPw,
  busy, demoBusy,
  onLogin, onDemo, onForgot,
}) {
  const isPortal = role !== "admin";
  const submitLabel = isPortal
    ? `Sign in as ${role === "partner" ? "Partner" : "Merchant"}`
    : "Sign in to live account";
  const demoLabel = isPortal
    ? `Try ${role === "partner" ? "Partner" : "Merchant"} demo`
    : "Try Admin demo";
  const formTestid = isPortal ? `${role}-login-form` : "login-form";
  const emailWrapTestid = isPortal ? `${role}-email-wrap` : "email-wrap";

  return (
    <form onSubmit={onLogin} className="space-y-5" data-testid={formTestid}>
      {isPortal && (
        <div className="flex items-start gap-3 p-3.5 rounded-sm border border-[var(--line-2)] bg-[var(--surface-2)]">
          <div className="h-8 w-8 rounded-sm bg-[var(--accent)]/10 text-[var(--accent)] grid place-items-center shrink-0">
            {role === "partner" ? <Users size={14} /> : <Store size={14} />}
          </div>
          <div className="text-[12px] text-[var(--muted)] leading-relaxed">
            {role === "partner"
              ? "Partner portal — view your merchants, monthly payouts, and statements."
              : "Merchant portal — view your volumes per payment gateway and the commercials you pay each PG."}
            <br />
            <span className="text-[var(--ink)]">Need access?</span> Ask your admin to grant you portal credentials.
          </div>
        </div>
      )}

      <Field label="Email" icon={Mail} testid={emailWrapTestid}>
        <input
          data-testid="login-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
          placeholder="you@company.com"
          autoFocus
        />
      </Field>

      <Field
        label="Password"
        icon={Lock}
        trailing={
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="text-[11px] text-[var(--muted)] hover:text-[var(--ink)] transition-colors inline-flex items-center gap-1"
            tabIndex={-1}
          >
            {showPw ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Show</>}
          </button>
        }
      >
        <input
          data-testid="login-password"
          type={showPw ? "text" : "password"}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputCls}
          placeholder="••••••••"
        />
      </Field>

      <button type="submit" disabled={busy} data-testid="login-submit" className={primaryBtn}>
        {busy && <Loader2 size={14} className="animate-spin" />}
        {submitLabel}
      </button>

      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
        <span className="h-px flex-1 bg-[var(--line)]" />or<span className="h-px flex-1 bg-[var(--line)]" />
      </div>

      <button type="button" disabled={demoBusy} onClick={onDemo} data-testid="login-demo-btn" className={secondaryBtn}>
        {demoBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {demoLabel}
        <span className="text-[10.5px] text-[var(--muted)] font-normal ml-auto">No password · No 2FA</span>
      </button>

      {isPortal ? (
        <button
          type="button"
          onClick={onForgot}
          className="w-full text-center text-[12px] text-[var(--accent)] hover:underline underline-offset-2"
        >
          Forgot password?
        </button>
      ) : (
        <div className="flex items-center justify-between pt-2">
          <button type="button" onClick={onForgot} data-testid="login-forgot" className="text-[12px] text-[var(--accent)] hover:underline underline-offset-2">
            Forgot password?
          </button>
          <div className="text-[11px] text-[var(--muted)]">Protected by 2FA</div>
        </div>
      )}
    </form>
  );
}
